import { PencilSquareIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { Editor } from "@tiptap/core";
import axios from "axios";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import { ignoreSuggestion } from "../../services/proofread/api";
import {
  PROOF_CATEGORY_LABEL,
  ProofSuggestion,
} from "../../services/proofread/types";
import Button from "../common/Button";
import { findSuggestion, ProofreadStorage } from "./ProofreadExtension";

interface ActiveSuggestion {
  suggestion: ProofSuggestion;
  from: number;
  to: number;
}

const EDGE = 12;
const GAP = 8;

/**
 * Gmail-style card anchored under the underline the officer clicked: a
 * category label, the replacement(s) as buttons, dismiss, and for spelling
 * lints an "Add to dictionary" action that writes to Common Spellings.
 *
 * Every interactive element cancels mousedown so the editor never loses
 * focus; the apply chain also refocuses explicitly, because Editor.tsx only
 * saves changes made while the editor is focused.
 */
export default function ProofreadPopover({ editor }: { editor: Editor | null }) {
  const [active, setActive] = useState<ActiveSuggestion | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(
    null,
  );
  const [adding, setAdding] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { reportPreferences, setReportPreferences } = useAuth();

  // Mirror the extension's active suggestion into React state.
  useEffect(() => {
    if (!editor) return;
    const sync = () => {
      const storage = editor.storage.proofread as ProofreadStorage | undefined;
      const id = storage?.activeId;
      if (!storage || !id) {
        setActive((prev) => (prev ? null : prev));
        return;
      }
      const suggestion = findSuggestion(editor.state, id);
      const range = storage.activeRange;
      if (!suggestion || !range) {
        setActive(null);
        return;
      }
      setActive((prev) =>
        prev?.suggestion.id === id && prev.from === range.from
          ? prev
          : { suggestion, from: range.from, to: range.to },
      );
    };
    sync();
    editor.on("transaction", sync);
    return () => {
      editor.off("transaction", sync);
    };
  }, [editor]);

  const close = useCallback(() => {
    if (!editor || editor.isDestroyed) return;
    const storage = editor.storage.proofread as ProofreadStorage | undefined;
    if (storage?.activeId) editor.commands.setActiveProofSuggestion(null);
  }, [editor]);

  // Typing closes the card (the underline follows the text on its own).
  useEffect(() => {
    if (!editor) return;
    const onUpdate = () => {
      setTimeout(close, 0);
    };
    editor.on("update", onUpdate);
    return () => {
      editor.off("update", onUpdate);
    };
  }, [editor, close]);

  const reposition = useCallback(() => {
    if (!editor || !active) return;
    let coords: { left: number; top: number; bottom: number };
    try {
      coords = editor.view.coordsAtPos(active.from);
    } catch {
      setPosition(null);
      return;
    }
    const el = ref.current;
    const width = el?.offsetWidth ?? 280;
    const height = el?.offsetHeight ?? 96;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = coords.bottom + GAP;
    if (top + height > vh - EDGE) top = coords.top - height - GAP;
    top = Math.max(EDGE, Math.min(top, vh - height - EDGE));

    let left = coords.left;
    left = Math.max(EDGE, Math.min(left, vw - width - EDGE));

    setPosition({ top, left });
  }, [editor, active]);

  useLayoutEffect(() => {
    reposition();
  }, [reposition]);

  useEffect(() => {
    if (!active) return;
    const handler = () => reposition();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [active, reposition]);

  // Outside click / Escape. Clicks on another underline are left to the
  // extension's handleClick, which swaps the active suggestion.
  useEffect(() => {
    if (!active) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (ref.current?.contains(target)) return;
      if (target.closest?.("[data-proof-id]")) return;
      close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [active, close]);

  const apply = (replacement: string) => {
    if (!editor || !active) return;
    editor
      .chain()
      .focus(undefined, { scrollIntoView: false })
      .applyProofSuggestion(active.suggestion.id, replacement)
      .run();
  };

  const dismiss = () => {
    if (!editor || !active) return;
    const { suggestion } = active;
    ignoreSuggestion(suggestion);
    editor.commands.dismissProofSuggestion(suggestion.id);
  };

  const addToDictionary = async () => {
    if (!editor || !active) return;
    const word = active.suggestion.original.trim();
    const existing = reportPreferences?.common_spellings || [];
    if (existing.includes(word)) {
      dismiss();
      return;
    }
    setAdding(true);
    try {
      const { data } = await axios.patch("/api/settings/report-preferences", {
        common_spellings: [...existing, word],
      });
      if (data?.preferences) setReportPreferences(data.preferences);
      editor.commands.dismissProofSuggestion(active.suggestion.id);
      toast.success(`Added “${word}” to your common spellings`);
    } catch (error) {
      console.error("Error adding word to common spellings:", error);
      toast.error("Couldn't save that word");
    } finally {
      setAdding(false);
    }
  };

  if (!active || !position) return null;

  const { suggestion } = active;
  const label = PROOF_CATEGORY_LABEL[suggestion.category];
  const showMessage =
    suggestion.message.length > 0 &&
    !(suggestion.category === "spelling" && suggestion.replacements.length > 0);

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      data-tour="proof-popover"
      aria-label={label}
      className="fixed z-[9999] w-max max-w-[min(90vw,360px)] bg-base-100 border border-base-300 rounded-xl shadow-xl animate-[fadeSlideUp_0.15s_ease-out]"
      style={{ top: position.top, left: position.left }}
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="flex items-center justify-between gap-4 px-3 pt-2.5 pb-1">
        <div className="flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-300">
          <PencilSquareIcon className="size-4" />
          {label}
        </div>
        <button
          type="button"
          onClick={dismiss}
          title="Dismiss"
          aria-label="Dismiss suggestion"
          className="text-base-content/40 hover:text-base-content/70 transition-colors"
        >
          <XCircleIcon className="size-5" />
        </button>
      </div>
      {showMessage && (
        <p className="px-3 m-0 text-xs text-base-content/60 max-w-[320px]">
          {suggestion.message}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2 px-3 pt-2 pb-3">
        {suggestion.replacements.map((replacement) => (
          <Button
            key={replacement}
            data-tour="proof-apply"
            variant="outline"
            size="sm"
            onClick={() => apply(replacement)}
            className="whitespace-pre"
          >
            {replacement === "" ? "Remove" : replacement}
          </Button>
        ))}
        {suggestion.category === "spelling" && (
          <button
            type="button"
            data-tour="proof-dictionary"
            disabled={adding}
            onClick={addToDictionary}
            className="text-xs text-base-content/60 hover:text-blue-600 dark:hover:text-blue-300 disabled:opacity-50 transition-colors"
          >
            {adding ? "Adding…" : "Add to dictionary"}
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
